/**
 * YouTube URL helpers for the lesson resource cards.
 *
 * There are three copies of "is this YouTube, and what is its id" in the app
 * already (ProxyPlayer, Embed.vue twice) and they do not agree. This is the one
 * the resource-card path uses, so the two questions a card asks about a URL —
 * "does this play in the YouTube embed?" and "what poster frame does it have?" —
 * cannot answer differently for the same link.
 */

// Deliberately the same test ResourceCard has always used. Widening it (to
// youtube-nocookie.com, say) would re-route existing cards from the native
// player to the embed, which is a behaviour change and not this fix's business.
const YOUTUBE_URL = /youtube\.com|youtu\.be/;

/** True when `url` is a link the card should treat as YouTube. */
export function isYoutubeUrl(url) {
  return YOUTUBE_URL.test(String(url || ''));
}

/**
 * The 11-character video id, or '' when the URL carries none.
 *
 * Accepts the shapes ProxyPlayer accepts (watch, youtu.be, /embed/, and the
 * youtube.html proxy the app itself builds) plus /shorts/, which the content
 * team can paste straight from the YouTube app.
 */
export function youtubeVideoId(url) {
  const raw = String(url || '').trim();
  if (!raw) return '';

  const path = raw.match(/(?:youtu\.be|\/embed|\/shorts|\/v)\/([^?&#/]+)/);
  if (path) return path[1];

  // watch?v=… and the youtube.html proxy both carry the id in `v`.
  const q = raw.indexOf('?');
  if (q === -1) return '';
  try {
    return new URLSearchParams(raw.slice(q + 1)).get('v') || '';
  } catch {
    return '';
  }
}

/**
 * The video's own poster frame, for a card whose author set no thumbnail.
 *
 * `hqdefault` and not `maxresdefault`: YouTube generates hqdefault for every
 * video, while maxresdefault 404s on anything never uploaded at 1080p — and a
 * 404 in an <img> is a broken card, which is worse than the placeholder it
 * replaced. hqdefault is 4:3 with the video letterboxed inside it; the card's
 * well is `aspect-video` with `object-cover`, so the bars crop back off.
 */
export function youtubeThumbnailUrl(url) {
  const id = youtubeVideoId(url);
  return id ? `https://img.youtube.com/vi/${id}/hqdefault.jpg` : '';
}
